import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shirt, Users, Trophy, Layers, Scissors, Ruler, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import QuoteDialog from "@/components/QuoteDialog";
import { SectionTitle } from "@/components/SectionTitle";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { api, fileUrl } from "@/lib/api";
import { buildWhatsAppLink, categoryQuoteMessage } from "@/lib/whatsapp";

const KITS = [
  { icon: Shirt, label: "Uniformes de time", desc: "Manto completo para futebol, futsal e demais modalidades." },
  { icon: Users, label: "Kits completos", desc: "Camiseta, shorts e meião com identidade do seu time." },
  { icon: Trophy, label: "Interclasse", desc: "Uniformes para turmas e campeonatos escolares." },
];

const FEATURES = [
  { icon: Layers, t: "Tecidos", d: "Opções de malhas esportivas com respiro e leveza." },
  { icon: Scissors, t: "Modelagens", d: "Modelagens masculina, feminina e infantil." },
  { icon: Ruler, t: "Golas", d: "Gola careca, polo e V conforme o estilo do time." },
];

const STEPS = [
  { t: "Briefing do time", d: "Escolha cores, escudo e estilo do uniforme." },
  { t: "Mockup", d: "Enviamos a prévia digital para aprovação." },
  { t: "Produção", d: "Sublimação e personalização de cada peça." },
  { t: "Entrega", d: "Combinamos entrega ou retirada do kit." },
];

const FAQ = [
  { q: "Fazem uniformes por sublimação?", a: "Sim, trabalhamos com personalização esportiva. Fale com a equipe no orçamento para detalhes do seu projeto." },
  { q: "Posso colocar nome e número?", a: "Sim. Envie a lista no orçamento e alinhamos a personalização de cada peça." },
  { q: "Atendem interclasse e escolas?", a: "Sim, atendemos turmas e campeonatos escolares. Solicite um orçamento com a quantidade aproximada." },
];

export default function SportsPage() {
  const { settings } = useSite();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [preset, setPreset] = useState("");
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    document.title = "K8 Sports | A identidade do seu time começa no uniforme.";
    api.get("/gallery?brand=sports").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  const openQuote = (p = "") => { setPreset(p); setQuoteOpen(true); };
  const whats = (label) => {
    if (!settings?.whatsapp_number) return openQuote(label);
    window.open(buildWhatsAppLink(settings.whatsapp_number, categoryQuoteMessage("K8 Sports", label)), "_blank");
  };

  return (
    <div className="App">
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={fileUrl(settings?.sports_hero_image)} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)] via-[var(--bg-primary)]/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--accent)]">Uniformes esportivos</span>
            <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.95] mt-4">
              A identidade do seu time começa no <span className="brand-gradient-text">uniforme.</span>
            </h1>
            <p className="text-[var(--text-muted)] text-lg mt-6 max-w-xl">
              Mantos de alta performance, kits completos e fardamentos personalizados para times, escolas e interclasse.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={() => openQuote()} data-testid="sports-hero-quote" className="btn-primary font-bold h-12 px-7">
                Solicitar orçamento <ArrowRight size={18} className="ml-2" />
              </Button>
              <a href="#galeria">
                <Button variant="outline" className="h-12 px-7 border-[var(--border)] bg-transparent hover:bg-[var(--bg-card)]">
                  Ver modelos
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="uniformes" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionTitle eyebrow="Modalidades" title="Uniformes e kits" />
        <div id="kits" className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {KITS.map((k, i) => (
            <div key={k.label} className="card-surface p-7 flex flex-col" data-testid={`sports-kit-${i}`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 accent-bar text-black">
                <k.icon size={22} />
              </div>
              <h3 className="font-head font-bold text-xl uppercase">{k.label}</h3>
              <p className="text-[var(--text-muted)] text-sm mt-2 flex-1">{k.desc}</p>
              <button onClick={() => whats(k.label)} data-testid={`sports-quote-${i}`}
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--accent)] hover:gap-3 transition-all">
                Orçamento no WhatsApp <MessageCircle size={16} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section id="galeria" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <SectionTitle eyebrow="Modelos" title="Galeria de uniformes"
          subtitle="Modelos reais da K8 Sports. Itens marcados como exemplo serão trocados por fotos reais." />
        {gallery.length === 0 ? (
          <div className="card-surface p-10 text-center text-[var(--text-muted)]" data-testid="sports-gallery-empty">
            A galeria de modelos será exibida assim que cadastrada no painel.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map((g) => (
              <div key={g.id} className="card-surface overflow-hidden group" data-testid={`sports-gallery-${g.id}`}>
                <div className="relative aspect-square overflow-hidden">
                  {g.image_url ? (
                    <img src={fileUrl(g.image_url)} alt={g.title || "Uniforme K8 Sports"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[var(--bg-secondary)] text-[var(--text-muted)] text-sm">Sem imagem</div>
                  )}
                  {g.is_placeholder && (
                    <span className="absolute top-2 left-2 text-[10px] font-bold uppercase bg-black/70 text-white px-2 py-1 rounded">Exemplo</span>
                  )}
                </div>
                {g.title && <div className="p-3 text-sm">{g.title}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-[var(--bg-secondary)] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle eyebrow="Detalhes técnicos" title="Tecidos, modelagens e golas" center />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.t} className="card-surface p-7 text-center">
                <f.icon className="mx-auto text-[var(--accent)] mb-4" />
                <h3 className="font-head font-bold text-lg uppercase">{f.t}</h3>
                <p className="text-[var(--text-muted)] text-sm mt-2">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="como-pedir" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <SectionTitle eyebrow="Passo a passo" title="Etapas do pedido" center />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.t} className="text-center" data-testid={`sports-step-${i}`}>
              <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center accent-bar text-black mb-4 font-head font-black text-xl">{i + 1}</div>
              <h3 className="font-head font-bold text-lg uppercase">{s.t}</h3>
              <p className="text-[var(--text-muted)] text-sm mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionTitle eyebrow="Dúvidas" title="Perguntas frequentes" center />
        <Accordion type="single" collapsible>
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`s-${i}`} className="border-[var(--border)]">
              <AccordionTrigger className="text-left font-semibold" data-testid={`sports-faq-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-[var(--text-muted)]">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="card-surface p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 accent-bar" />
          <div className="relative">
            <h2 className="font-head text-3xl sm:text-4xl font-black uppercase">Monte o uniforme do seu time</h2>
            <p className="text-[var(--text-muted)] mt-3 max-w-xl mx-auto">Solicite um orçamento e leve a identidade do seu time para dentro de campo.</p>
            <Button onClick={() => openQuote()} data-testid="sports-final-quote" className="btn-primary font-bold h-12 px-8 mt-6">Solicitar orçamento</Button>
          </div>
        </div>
        <Link to="/" className="mt-8 block">
          <div className="rounded-2xl p-8 border border-[var(--border)] bg-[var(--bg-card)] flex items-center justify-between group">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[var(--accent)]">Conheça também</span>
              <h3 className="font-head text-2xl font-black mt-1 brand-gradient-text">K8 Stamp</h3>
              <p className="text-[var(--text-muted)] text-sm mt-1">Uniformes empresariais, camisetas, brindes e materiais gráficos.</p>
            </div>
            <ArrowRight className="group-hover:translate-x-1 transition-transform text-[var(--accent)]" size={28} />
          </div>
        </Link>
      </section>

      <Footer brand="sports" />
      <FloatingWhatsApp brandName="K8 Sports" />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} brand="sports" presetProduct={preset} />
    </div>
  );
}
